import {
    FunctionComponent,
    PropsWithChildren,
    ReactElement,
    useMemo,
} from "react";
import { useParams } from "react-router";
import Markdown from "markdown-to-jsx";
import { useUser } from "@/contexts/user";
import { canAccessAllOrgsAndDevices } from "@/utils/user-permission";
import { PageTitle } from "@/contexts/page-title";
import { BreadcrumbSetter } from "@/contexts/useBreadcrumbs";
import {
    BodyBold,
    BodyDefault,
    HeadingCard,
    HeadingPageTitle,
    HeadingSection,
    HeadingSubsection,
    Link,
} from "@/components/ui/typography";
import { useHelpPost } from "../use-help-post";

const HelpTopicPage: FunctionComponent = (): ReactElement => {
    const { slug } = useParams<{ slug: string }>();
    const { user } = useUser();

    const isStaff = useMemo(() => canAccessAllOrgsAndDevices(user), [user]);

    const { post, isError } = useHelpPost({ slug });

    if (isError)
        return (
            <div>Failed to fetch help topic. Please refresh to try again</div>
        );

    if (!post || !user) {
        return <div>loading...</div>;
    }

    const hasAccess = post.isPublished && (isStaff || !post.staffOnly);

    if (!hasAccess) {
        return <div>You do not have access to this help topic.</div>;
    }

    return (
        <div style={{ maxWidth: "700px", margin: "0 auto" }}>
            <PageTitle title={post.title}></PageTitle>
            <BreadcrumbSetter
                breadcrumbsToSet={[
                    { breadcrumbKey: ":slug", value: post.title },
                ]}
            />
            <PostMarkdown>{post.body}</PostMarkdown>
        </div>
    );
};

export const PostMarkdown = ({ children }: PropsWithChildren) => {
    return (
        <Markdown
            className="prose"
            options={{
                overrides: {
                    img: {
                        component: ({ ...rest }) => {
                            const openImage = () => {
                                window.open(
                                    rest.src,
                                    "_blank",
                                    "noopener,noreferrer",
                                );
                            };

                            const handleKeyPress = (
                                event: React.KeyboardEvent,
                            ) => {
                                if (
                                    event.key === "Enter" ||
                                    event.key === " "
                                ) {
                                    event.preventDefault();
                                    openImage();
                                }
                            };

                            return (
                                <img
                                    {...rest}
                                    style={{
                                        maxWidth: "100%",
                                        cursor: "pointer",
                                    }}
                                    onClick={openImage}
                                    onKeyDown={handleKeyPress}
                                    tabIndex={0}
                                    role="button"
                                    aria-label={`View full size image: ${rest.alt || rest.title || "Image"}`}
                                    title="Click to view full size"
                                />
                            );
                        },
                    },
                    h1: {
                        component: ({ ...rest }) => (
                            <HeadingPageTitle {...rest} />
                        ),
                    },
                    h2: {
                        component: ({ ...rest }) => (
                            <HeadingSection {...rest} />
                        ),
                    },
                    h3: {
                        component: ({ ...rest }) => <HeadingCard {...rest} />,
                    },
                    h4: {
                        component: ({ ...rest }) => (
                            <HeadingSubsection {...rest} />
                        ),
                    },
                    h5: {
                        component: ({ ...rest }) => (
                            <h5
                                {...rest}
                                className="font-heading text-default-color text-large font-boldest leading-default"
                            />
                        ),
                    },
                    p: {
                        component: ({ ...rest }) => (
                            <BodyDefault
                                {...rest}
                                className="[&:has(>img)+p]:text-small [&:has(>img)+p]:text-subtle [&:has(>img)+p]:text-center"
                            />
                        ),
                    },
                    ul: {
                        component: ({ ...rest }) => (
                            <ul
                                {...rest}
                                className="font-body text-default-color text-default font-default leading-default list-disc"
                            />
                        ),
                    },
                    ol: {
                        component: ({ ...rest }) => (
                            <ol
                                {...rest}
                                className="font-body text-default-color text-default font-default leading-default list-decimal"
                            />
                        ),
                    },
                    strong: {
                        component: ({ ...rest }) => (
                            <BodyBold as="strong" {...rest} />
                        ),
                    },
                    a: {
                        component: ({ ...rest }) => <Link {...rest} />,
                    },
                },
            }}
        >
            {children as string}
        </Markdown>
    );
};

export default HelpTopicPage;
